import { useEffect } from "react";

type Props = {
  id: string;
  value: string;
  portion?: "head" | "body";
  noscript?: boolean;
  /**
   * Delay script injection by this many milliseconds after mount.
   * Uses requestIdleCallback when available, falling back to setTimeout.
   * Useful for deferring non-critical third-party scripts past TTI.
   */
  delayMs?: number;
};

export default function useScriptFunction({
  id,
  value,
  portion = "head",
  noscript = false,
  delayMs,
}: Props) {
  useEffect(() => {
    const inject = () => {
      if (document.getElementById(id)) {
        return;
      }

      if (!value) return;

      let script;
      if (!noscript) {
        script = document.createElement("script");
        script.type = "text/javascript";
      } else {
        script = document.createElement("noscript");
      }

      script.innerHTML = value;
      script.id = id;
      document[portion].appendChild(script);
    };

    if (delayMs !== undefined) {
      if ("requestIdleCallback" in window) {
        requestIdleCallback(inject, { timeout: delayMs });
      } else {
        setTimeout(inject, delayMs);
      }
    } else {
      inject();
    }
  }, [id, value, portion, noscript, delayMs]);
}
